"use client";

import { useState } from "react";
import { motion, useReducedMotion } from "@/lib/motion";
import { TrendingUp } from "@/components/icons";
import { trackEvent } from "@/lib/posthog-client";
import { formatGb } from "./data";
import type { PeriodDataset } from "./types";

const DOMAINS_COLLAPSED_COUNT = 10;

interface TrendsViewProps {
  dataset: PeriodDataset;
  factor: number;
}

export function TrendsView({ dataset, factor }: TrendsViewProps) {
  const prefersReducedMotion = useReducedMotion();
  const values = dataset.trends.map((p) => p.gb * factor);
  const total = values.reduce((sum, v) => sum + v, 0);
  const max = Math.max(...values);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-muted-foreground mb-1 text-[10px] font-bold tracking-wider uppercase">
            Data transferred
          </p>
          <p className="text-foreground text-2xl font-bold tracking-tight sm:text-3xl">
            {formatGb(total)}
          </p>
        </div>
        <span
          className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold"
          style={{
            color: "var(--brand-green)",
            borderColor: `color-mix(in srgb, var(--brand-green) 30%, transparent)`,
            backgroundColor: `color-mix(in srgb, var(--brand-green) 8%, transparent)`,
          }}
        >
          <TrendingUp size={12} aria-hidden="true" />+{dataset.deltaPct}%
          <span className="font-normal opacity-70">{dataset.deltaLabel}</span>
        </span>
      </div>

      <div className="flex min-h-0 flex-1 items-end gap-1 sm:gap-1.5" aria-hidden="true">
        {dataset.trends.map((point, i) => {
          const value = values[i];
          const isPeak = value === max;
          return (
            <div key={point.label} className="group relative flex h-full flex-1 items-end">
              <span className="text-foreground/90 border-border bg-card pointer-events-none absolute -top-7 left-1/2 z-10 -translate-x-1/2 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap opacity-0 transition-opacity group-hover:opacity-100">
                {formatGb(value)}
              </span>
              <motion.div
                className="w-full rounded-t-sm"
                style={{
                  height: `${(value / max) * 100}%`,
                  transformOrigin: "bottom",
                  background: isPeak
                    ? `linear-gradient(to top, color-mix(in srgb, var(--brand-yellow) 40%, transparent), color-mix(in srgb, var(--brand-yellow) 95%, transparent))`
                    : `linear-gradient(to top, color-mix(in srgb, var(--foreground) 8%, transparent), color-mix(in srgb, var(--brand-green) 45%, transparent))`,
                }}
                initial={prefersReducedMotion ? false : { scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ duration: 0.45, delay: prefersReducedMotion ? 0 : i * 0.025 }}
              />
            </div>
          );
        })}
      </div>

      <div className="mt-2 flex gap-1 sm:gap-1.5" aria-hidden="true">
        {dataset.trends.map((point, i) => (
          <div key={point.label} className="flex-1 text-center">
            <span className="text-muted-foreground/70 text-[9px] sm:text-[10px]">
              {i % dataset.labelEvery === 0 ? point.label : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface DomainsViewProps {
  dataset: PeriodDataset;
  factor: number;
}

export function DomainsView({ dataset, factor }: DomainsViewProps) {
  const prefersReducedMotion = useReducedMotion();
  const [expanded, setExpanded] = useState(false);
  const max = dataset.domains[0].gb * factor;
  const visible = expanded ? dataset.domains : dataset.domains.slice(0, DOMAINS_COLLAPSED_COUNT);

  function handleToggle() {
    setExpanded((prev) => {
      trackEvent("analytics_feature_domains_expanded", {
        period: dataset.key,
        expanded: !prev,
      });
      return !prev;
    });
  }

  return (
    <div className="flex h-full flex-col">
      <p className="text-muted-foreground mb-4 text-[10px] font-bold tracking-wider uppercase">
        Top domains by volume
      </p>
      <ul className="toc-scroll min-h-0 flex-1 space-y-2.5 overflow-y-auto pr-1.5">
        {visible.map((row, i) => {
          const value = row.gb * factor;
          const topThree = i < 3;
          return (
            <li key={row.domain} className="flex items-center gap-3">
              <span className="text-muted-foreground/60 w-5 shrink-0 text-right font-mono text-[10px]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-foreground/85 w-28 shrink-0 truncate text-xs font-medium sm:w-32">
                {row.domain}
              </span>
              <div className="bg-foreground/[0.05] h-2 flex-1 overflow-hidden rounded-full">
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    transformOrigin: "left",
                    width: `${(value / max) * 100}%`,
                    background: topThree
                      ? `linear-gradient(to right, color-mix(in srgb, var(--brand-green) 40%, transparent), color-mix(in srgb, var(--brand-green) 85%, transparent))`
                      : "linear-gradient(to right, color-mix(in srgb, var(--foreground) 12%, transparent), color-mix(in srgb, var(--foreground) 30%, transparent))",
                  }}
                  initial={prefersReducedMotion ? false : { scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{
                    duration: 0.5,
                    delay: prefersReducedMotion ? 0 : Math.min(i, DOMAINS_COLLAPSED_COUNT) * 0.05,
                  }}
                />
              </div>
              <span className="text-muted-foreground w-16 shrink-0 text-right font-mono text-[11px]">
                {formatGb(value)}
              </span>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        onClick={handleToggle}
        className="border-border text-muted-foreground hover:text-foreground/80 hover:bg-foreground/[0.04] mt-3 w-full shrink-0 rounded-lg border py-1.5 text-[11px] font-medium transition-colors"
      >
        {expanded
          ? `Show top ${DOMAINS_COLLAPSED_COUNT}`
          : `Show all ${dataset.domains.length} domains`}
      </button>
    </div>
  );
}

interface HistoryViewProps {
  dataset: PeriodDataset;
  accessPoints: string[];
  apIndex: number;
}

export function HistoryView({ dataset, accessPoints, apIndex }: HistoryViewProps) {
  const prefersReducedMotion = useReducedMotion();
  const rows =
    apIndex === 0 ? dataset.history : dataset.history.filter((r) => r.apIndex === apIndex);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex shrink-0 items-center justify-between">
        <p className="text-muted-foreground text-[10px] font-bold tracking-wider uppercase">
          Connection history
        </p>
        <span className="text-muted-foreground flex items-center gap-1.5 text-[10px] font-semibold tracking-wider uppercase">
          <motion.span
            className="size-1.5 rounded-full"
            style={{ backgroundColor: "var(--brand-green)" }}
            initial={{ opacity: 1 }}
            animate={prefersReducedMotion ? { opacity: 1 } : { opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            aria-hidden="true"
          />
          Live
        </span>
      </div>

      <div className="border-border flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border">
        <div className="text-muted-foreground border-border bg-foreground/[0.03] grid shrink-0 grid-cols-[64px_1fr_60px_48px] gap-2 border-b px-3 py-2 text-[10px] font-bold tracking-wider uppercase sm:grid-cols-[72px_1fr_1fr_64px_52px]">
          <span>Time</span>
          <span>Domain</span>
          <span className="hidden sm:block">Access point</span>
          <span className="text-right">Data</span>
          <span className="text-right">Status</span>
        </div>
        <ul className="toc-scroll min-h-0 flex-1 overflow-y-auto">
          {rows.map((row, i) => (
            <motion.li
              key={row.id}
              className="border-border/60 grid grid-cols-[64px_1fr_60px_48px] items-center gap-2 border-b px-3 py-2 last:border-b-0 sm:grid-cols-[72px_1fr_1fr_64px_52px]"
              initial={prefersReducedMotion ? false : { opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: prefersReducedMotion ? 0 : i * 0.05 }}
            >
              <span className="text-muted-foreground font-mono text-[11px]">{row.time}</span>
              <span className="text-foreground/85 truncate text-xs font-medium">{row.domain}</span>
              <span className="text-muted-foreground hidden truncate font-mono text-[11px] sm:block">
                {accessPoints[row.apIndex]}
              </span>
              <span className="text-muted-foreground text-right font-mono text-[11px]">
                {row.size}
              </span>
              <span className="text-right">
                <span
                  className="inline-block rounded px-1.5 py-0.5 font-mono text-[10px] font-bold"
                  style={{
                    color: "var(--brand-green)",
                    backgroundColor: `color-mix(in srgb, var(--brand-green) 10%, transparent)`,
                  }}
                >
                  {row.status}
                </span>
              </span>
            </motion.li>
          ))}
        </ul>
      </div>
    </div>
  );
}
